import { useCallback, useEffect, useState } from "react";
import { T, base } from "../lib/theme";
import { apiFetch } from "../lib/api";
import { Btn, Card, Icon } from "../components";

// ── Cache helpers ──────────────────────────────────────────────────
const CACHE_KEY = "wb_insights_cache_v1";
function saveCache(data) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch {}
}
function loadCache() {
  try { const d = localStorage.getItem(CACHE_KEY); return d ? JSON.parse(d) : null; } catch { return null; }
}

// ── Score ring ─────────────────────────────────────────────────────
const ScoreRing = ({ score }) => {
  const r    = 54;
  const circ = 2 * Math.PI * r;
  const fill = ((100 - score) / 100) * circ;
  const color = score >= 75 ? T.emerald : score >= 50 ? T.amber : T.rose;
  return (
    <div style={{ position: "relative", width: 140, height: 140 }}>
      <svg width="140" height="140" style={{ transform: "rotate(-90deg)" }}>
        <circle cx="70" cy="70" r={r} fill="none" stroke={T.border2} strokeWidth="10" />
        <circle cx="70" cy="70" r={r} fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={circ} strokeDashoffset={fill}
          strokeLinecap="round" style={{ transition: "stroke-dashoffset 1s ease" }} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: 30, fontWeight: 800, color, fontFamily: base.mono, lineHeight: 1 }}>{score}</div>
        <div style={{ fontSize: 10, color: T.text3, marginTop: 3, fontWeight: 600 }}>/ 100</div>
      </div>
    </div>
  );
};

// ── Chip list ──────────────────────────────────────────────────────
const ChipList = ({ items, color }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
    {(items || []).map((item, i) => (
      <div key={i} style={{ fontSize: 12, padding: "5px 12px", borderRadius: 20, background: `${color}18`, color, border: `1px solid ${color}33`, lineHeight: 1.4 }}>
        {item}
      </div>
    ))}
  </div>
);

// ── KB Suggestion card ─────────────────────────────────────────────
const KBSuggestion = ({ line, token, onAdded }) => {
  const [adding, setAdding] = useState(false);
  const [added,  setAdded]  = useState(false);

  const addToKB = async () => {
    setAdding(true);
    try {
      const current = await apiFetch("/api/knowledge", {}, token);
      const updated = (current.knowledge || "") + "\n" + line;
      await apiFetch("/api/set-knowledge", { method: "POST", body: JSON.stringify({ text: updated }) }, token);
      setAdded(true);
      onAdded?.(line);
    } catch {}
    setAdding(false);
  };

  return (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "10px 14px", borderRadius: 10, background: T.bg0, border: `1px solid ${added ? T.emerald+"44" : T.border1}`, marginBottom: 8, transition: "border-color 0.2s" }}>
      <div style={{ flex: 1, fontSize: 12, color: T.text2, lineHeight: 1.6, fontFamily: base.mono }}>{line}</div>
      <button onClick={addToKB} disabled={adding || added}
        style={{ flexShrink: 0, padding: "5px 12px", borderRadius: 8, border: `1px solid ${added ? T.emerald : T.border2}`, background: added ? T.emeraldDim : "none", color: added ? T.emerald : T.text3, fontSize: 11, fontWeight: 600, cursor: added ? "default" : "pointer", fontFamily: base.font, transition: "all 0.2s", whiteSpace: "nowrap" }}>
        {adding ? "Adding..." : added ? "✓ Added" : "+ Add to KB"}
      </button>
    </div>
  );
};

// ── Credit widget ──────────────────────────────────────────────────
const CreditWidget = ({ credits }) => {
  if (!credits) return null;
  const { used, limit, remaining, resetAt, nextAutoRefreshAt } = credits;
  const pct = limit === -1 ? 0 : Math.min(100, Math.round((used / (limit || 1)) * 100));
  const col = pct >= 90 ? T.rose : pct >= 70 ? T.amber : T.emerald;

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString([], { month: "short", day: "numeric" }) : "—";
  const fmtCountdown = (d) => {
    if (!d) return null;
    const diff = new Date(d) - Date.now();
    if (diff <= 0) return "Refreshing soon";
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return h > 0 ? `Next auto-refresh in ${h}h ${m}m` : `Next auto-refresh in ${m}m`;
  };

  return (
    <div style={{ padding: "12px 16px", borderRadius: 12, background: T.bg2, border: `1px solid ${T.border1}`, marginBottom: 20, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: T.text3, whiteSpace: "nowrap" }}>AI Credits</div>
      <div style={{ flex: 1, minWidth: 120 }}>
        <div style={{ height: 5, borderRadius: 99, background: T.border1, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${pct}%`, background: col, borderRadius: 99, transition: "width 0.6s ease" }} />
        </div>
      </div>
      <div style={{ fontSize: 12, fontFamily: base.mono, color: col, fontWeight: 700, whiteSpace: "nowrap" }}>
        {used} / {limit === -1 ? "∞" : limit}
      </div>
      <div style={{ fontSize: 11, color: remaining === 0 ? T.rose : T.text3, whiteSpace: "nowrap" }}>
        {remaining === 0
          ? `Limit reached · resets ${fmtDate(resetAt)}`
          : `${remaining} left · resets ${fmtDate(resetAt)}`}
      </div>
      {fmtCountdown(nextAutoRefreshAt) && (
        <div style={{ fontSize: 11, color: T.text3, whiteSpace: "nowrap" }}>
          {fmtCountdown(nextAutoRefreshAt)}
        </div>
      )}
    </div>
  );
};

// ── Main InsightsPage ──────────────────────────────────────────────
export default function InsightsPage({ token, showToast, setPage }) {
  // Initialise from localStorage immediately — no blank flash on refresh
  const [data,       setData]       = useState(() => loadCache());
  const [loading,    setLoading]    = useState(false);
  const [lastRun,    setLastRun]    = useState(null);
  const [addedLines, setAddedLines] = useState([]);
  const [fromCache,  setFromCache]  = useState(false);
  const [error,      setError]      = useState(null); // "credit_exhausted" | null

  const runAnalysis = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const d = await apiFetch("/api/insights", {}, token);

      // Credit exhausted — keep cached data visible, just show warning
      if (d?.creditExhausted) {
        setError("credit_exhausted");
        // Merge fresh credits info into cached data so widget stays accurate
        if (d.credits) setData(prev => prev ? { ...prev, credits: d.credits } : prev);
        if (!silent) setLoading(false);
        return;
      }

      if (d?.error) {
        showToast?.(d.error, "error");
        if (!silent) setLoading(false);
        return;
      }

      setData(d);
      setFromCache(d?.fromCache || false);
      setLastRun(new Date());
      setAddedLines([]);
      saveCache(d);
    } catch {
      if (!silent) showToast?.("Failed to run analysis", "error");
    }
    if (!silent) setLoading(false);
  }, [token, showToast]);

  // On mount: show cache instantly, silently check if refresh is due
  useEffect(() => {
    const cached = loadCache();
    if (!cached) {
      runAnalysis(false); // no cache — do visible load
    } else {
      const nextRefresh = cached?.nextAutoRefreshAt;
      if (!nextRefresh || new Date() >= new Date(nextRefresh)) {
        runAnalysis(true); // refresh due — run silently, cached data stays visible
      }
      // else: not due — just show cached data, do nothing
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const score      = data?.score ?? 0;
  const ai         = data?.aiAnalysis;
  const stats      = data?.stats;
  const breakdown  = data?.scoreBreakdown || [];
  const credits    = data?.credits;
  const scoreColor = score >= 75 ? T.emerald : score >= 50 ? T.amber : T.rose;
  const scoreLabel = score >= 75 ? "Performing well" : score >= 50 ? "Needs improvement" : "Needs urgent attention";

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "26px 28px 60px" }}>

      {/* ── Header ──────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text1 }}>AI Insights & Analysis</div>
          <div style={{ fontSize: 13, color: T.text3, marginTop: 3 }}>
            AI reads your conversations and tells you exactly what to improve
          </div>
          {(lastRun || data?.generatedAt) && (
            <div style={{ fontSize: 11, color: T.text3, marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
              Last analysed: {new Date(lastRun || data.generatedAt).toLocaleTimeString()}
              {fromCache && (
                <span style={{ padding: "1px 6px", borderRadius: 4, background: `${T.amber}18`, color: T.amber, fontSize: 10, fontWeight: 600 }}>cached</span>
              )}
            </div>
          )}
        </div>
        <Btn variant="primary" onClick={() => runAnalysis(false)} disabled={loading || error === "credit_exhausted"}>
          {loading
            ? <><div style={{ width: 13, height: 13, border: "2px solid rgba(0,0,0,0.2)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} /> Analysing...</>
            : error === "credit_exhausted"
            ? <>No Credits</>
            : <><span>🔍</span> Run Analysis</>}
        </Btn>
      </div>

      {/* ── Credit widget ────────────────────────────────────── */}
      <CreditWidget credits={credits} />

      {/* ── Credit exhausted warning ─────────────────────────── */}
      {error === "credit_exhausted" && (
        <div style={{ padding: "14px 16px", borderRadius: 12, background: `${T.rose}10`, border: `1px solid ${T.rose}33`, marginBottom: 20, display: "flex", gap: 10 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.rose} strokeWidth="2" style={{flexShrink:0,marginTop:1}}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.rose }}>Monthly credit limit reached</div>
            <div style={{ fontSize: 12, color: T.text2, marginTop: 3, lineHeight: 1.6 }}>
              Your AI Insights credits have been used up for this month. They reset on the 1st.
              {data && " Your last analysis is shown below."}
            </div>
          </div>
        </div>
      )}

      {/* ── Silent refresh indicator (has data, re-fetching) ── */}
      {loading && data && (
        <div style={{ padding: "8px 14px", borderRadius: 8, background: `${T.emerald}10`, border: `1px solid ${T.emerald}25`, marginBottom: 16, fontSize: 12, color: T.emerald }}>
          Refreshing analysis in background...
        </div>
      )}

      {/* ── Empty state ───────────────────────────────────────── */}
      {!data && !loading && (
        <Card style={{ textAlign: "center", padding: "56px 24px" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16, opacity: 0.2 }}><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></div>
          <div style={{ fontSize: 15, fontWeight: 600, color: T.text2, marginBottom: 8 }}>No analysis yet</div>
          <div style={{ fontSize: 13, color: T.text3, marginBottom: 24, lineHeight: 1.7, maxWidth: 400, margin: "0 auto 24px" }}>
            Click "Run Analysis" to get AI-powered insights about your bot's performance, knowledge gaps, and what customers are asking.
          </div>
          <Btn variant="primary" onClick={() => runAnalysis(false)}>🔍 Run Analysis Now</Btn>
        </Card>
      )}

      {/* ── Loading skeleton — only when NO cached data ───────── */}
      {loading && !data && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {[1,2,3].map(i => (
            <div key={i} className="skeleton" style={{ height: 120, borderRadius: 14 }} />
          ))}
          <div style={{ textAlign: "center", fontSize: 13, color: T.text3, marginTop: 8 }}>
            AI is reading your conversations...
          </div>
        </div>
      )}

      {/* ── Main content — visible even while silently refreshing */}
      {data && (
        <>
          {/* Row 1: Score + Breakdown */}
          <div className="grid-2" style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 20, marginBottom: 20 }}>
            <Card style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px 32px", minWidth: 220 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.text3, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 14 }}>Bot Performance Score</div>
              <ScoreRing score={score} />
              <div style={{ fontSize: 13, fontWeight: 700, color: scoreColor, marginTop: 14 }}>{scoreLabel}</div>
              <div style={{ fontSize: 11, color: T.text3, marginTop: 4, textAlign: "center" }}>Based on settings, knowledge & conversations</div>
            </Card>

            <Card>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text1, marginBottom: 16 }}>Score Breakdown</div>
              {breakdown.map((b, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: i < breakdown.length - 1 ? `1px solid ${T.border1}` : "none" }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: b.points < 0 ? T.rose : T.emerald, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: T.text1 }}>{b.area}</div>
                    <div style={{ fontSize: 11, color: T.text3, marginTop: 1 }}>{b.reason}</div>
                  </div>
                  <div style={{ fontFamily: base.mono, fontSize: 13, fontWeight: 700, color: b.points < 0 ? T.rose : T.emerald }}>
                    {b.points < 0 ? b.points : "✓"}
                  </div>
                </div>
              ))}
            </Card>
          </div>

          {/* Row 2: Stats */}
          <div className="grid-4" style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12, marginBottom: 20 }}>
            {[
              { label: "Active Chats",   val: stats?.totalChats    || 0, color: T.sky     },
              { label: "Total Messages", val: stats?.totalMessages || 0, color: T.emerald },
              { label: "Human Handoffs", val: stats?.humanHandoffs || 0, color: T.violet  },
              { label: "Handoff Rate",   val: `${stats?.handoffRate || 0}%`, color: stats?.handoffRate > 30 ? T.rose : T.amber },
              { label: "Unread",         val: stats?.unread        || 0, color: T.amber   },
            ].map(({ label, val, color }) => (
              <div key={label} style={{ padding: "14px 16px", borderRadius: 12, background: T.bg2, border: `1px solid ${T.border1}` }}>
                <div style={{ fontSize: 22, fontWeight: 800, color, fontFamily: base.mono, lineHeight: 1 }}>{val}</div>
                <div style={{ fontSize: 11, color: T.text3, marginTop: 5 }}>{label}</div>
              </div>
            ))}
          </div>

          {/* AI Analysis */}
          {ai && (
            <>
              {ai.summary && (
                <Card style={{ marginBottom: 20, background: T.skyDim, border: `1px solid rgba(56,189,248,0.2)` }}>
                  <div style={{ display: "flex", gap: 12 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.sky} strokeWidth="1.8" style={{flexShrink:0,marginTop:1}}><rect x="3" y="8" width="18" height="13" rx="2"/><path d="M12 3v5"/><circle cx="12" cy="3" r="1.5"/><path d="M7 13h2"/><path d="M15 13h2"/><path d="M9 17h6"/></svg>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: T.sky, marginBottom: 6 }}>AI Assessment</div>
                      <div style={{ fontSize: 13, color: T.text2, lineHeight: 1.7 }}>{ai.summary}</div>
                    </div>
                  </div>
                </Card>
              )}

              <div className="grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
                {ai.unansweredTopics?.length > 0 && (
                  <Card>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.rose, marginBottom: 12, display: "flex", alignItems: "center", gap: 7 }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.rose} strokeWidth="2.2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg> Topics Bot Couldn't Answer
                    </div>
                    <div style={{ fontSize: 12, color: T.text3, marginBottom: 10 }}>These are gaps in your knowledge base</div>
                    <ChipList items={ai.unansweredTopics} color={T.rose} />
                  </Card>
                )}

                {ai.frequentQuestions?.length > 0 && (
                  <Card>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.amber, marginBottom: 12, display: "flex", alignItems: "center", gap: 7 }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.amber} strokeWidth="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.5"/></svg> Most Repeated Questions
                    </div>
                    <div style={{ fontSize: 12, color: T.text3, marginBottom: 10 }}>Customers ask these most — make sure your KB covers them</div>
                    <ChipList items={ai.frequentQuestions} color={T.amber} />
                  </Card>
                )}

                {ai.quickWins?.length > 0 && (
                  <Card>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.emerald, marginBottom: 12, display: "flex", alignItems: "center", gap: 7 }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.emerald} strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg> Quick Wins
                    </div>
                    <div style={{ fontSize: 12, color: T.text3, marginBottom: 10 }}>Fastest things you can do to improve performance</div>
                    {(ai.quickWins || []).map((win, i) => (
                      <div key={i} style={{ display: "flex", gap: 10, padding: "8px 0", borderBottom: i < ai.quickWins.length - 1 ? `1px solid ${T.border1}` : "none" }}>
                        <div style={{ width: 20, height: 20, borderRadius: "50%", background: T.emeraldDim, border: `1px solid ${T.emerald}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: T.emerald, flexShrink: 0 }}>{i + 1}</div>
                        <div style={{ fontSize: 13, color: T.text1, lineHeight: 1.5 }}>{win}</div>
                      </div>
                    ))}
                  </Card>
                )}

                {ai.botBehaviourIssues?.length > 0 && (
                  <Card>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.violet, marginBottom: 12, display: "flex", alignItems: "center", gap: 7 }}>
                      <span>🎭</span> Bot Behaviour Issues
                    </div>
                    <div style={{ fontSize: 12, color: T.text3, marginBottom: 10 }}>Tone, language, or style problems noticed</div>
                    {(ai.botBehaviourIssues || []).map((issue, i) => (
                      <div key={i} style={{ display: "flex", gap: 10, padding: "7px 0", borderBottom: i < ai.botBehaviourIssues.length - 1 ? `1px solid ${T.border1}` : "none", fontSize: 13, color: T.text1, lineHeight: 1.5 }}>
                        <span style={{ color: T.violet, flexShrink: 0 }}>•</span>{issue}
                      </div>
                    ))}
                  </Card>
                )}
              </div>

              {/* KB Suggestions — full width, one-click add */}
              {ai.suggestedKBLines?.length > 0 && (
                <Card style={{ marginBottom: 20 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: T.text1, display: "flex", alignItems: "center", gap: 7 }}>
                        <span>📝</span> Suggested Knowledge Base Additions
                      </div>
                      <div style={{ fontSize: 12, color: T.text3, marginTop: 4 }}>
                        AI wrote these lines based on what customers asked — click "+ Add to KB" to add them instantly
                      </div>
                    </div>
                    {addedLines.length > 0 && (
                      <div style={{ fontSize: 11, color: T.emerald, fontWeight: 600 }}>{addedLines.length} added</div>
                    )}
                  </div>
                  {(ai.suggestedKBLines || [])
                    .filter(line => !addedLines.includes(line))
                    .map((line, i) => (
                      <KBSuggestion key={i} line={line} token={token}
                        onAdded={(l) => {
                          setAddedLines(p => [...p, l]);
                          showToast?.("Added to knowledge base ✓");
                        }} />
                    ))}
                  {addedLines.length > 0 && (
                    <div style={{ padding: "10px 14px", borderRadius: 10, background: T.emeraldDim, border: `1px solid rgba(99,102,241,0.2)`, fontSize: 12, color: T.emerald, marginTop: 8 }}>
                      {addedLines.length} suggestion{addedLines.length !== 1 ? "s" : ""} added to your knowledge base. The AI will use them from the next message.
                    </div>
                  )}
                </Card>
              )}

              {ai.knowledgeBaseGaps?.length > 0 && (
                <Card style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.amber, marginBottom: 12, display: "flex", alignItems: "center", gap: 7 }}>
                    <span>🕳️</span> Knowledge Base Gaps
                  </div>
                  <div style={{ fontSize: 12, color: T.text3, marginBottom: 12 }}>Topics you should add to your knowledge base</div>
                  <ChipList items={ai.knowledgeBaseGaps} color={T.amber} />
                  <div style={{ marginTop: 14 }}>
                    <Btn variant="ghost" size="sm" onClick={() => setPage?.("knowledge")} style={{ border: `1px solid ${T.amber}55`, color: T.amber }}>
                      📝 Go to Knowledge Base →
                    </Btn>
                  </div>
                </Card>
              )}
            </>
          )}

          {/* Quick Actions */}
          <Card>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text1, marginBottom: 14 }}>Quick Actions</div>
            <div className="grid-3" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
              {[
                { icon: "📝", label: "Update Knowledge Base",  sub: "Add missing info the bot needs",    page: "knowledge", color: T.emerald },
                { icon: "🛡️", label: "Review Safety Settings", sub: "Check anti-ban configuration",      page: "safety",    color: T.amber   },
                { icon: "🕐", label: "Configure Schedule",     sub: "Set active hours for auto-replies", page: "schedule",  color: T.sky     },
              ].map(({ icon, label, sub, page, color }) => (
                <div key={page} onClick={() => setPage?.(page)}
                  style={{ padding: "14px 16px", borderRadius: 12, background: T.bg0, border: `1px solid ${T.border1}`, cursor: "pointer", transition: "all 0.15s" }}
                  onMouseOver={e => { e.currentTarget.style.borderColor = color + "55"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                  onMouseOut={e  => { e.currentTarget.style.borderColor = T.border1;   e.currentTarget.style.transform = "none"; }}>
                  <div style={{ fontSize: 20, marginBottom: 8 }}>{icon}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.text1 }}>{label}</div>
                  <div style={{ fontSize: 11, color: T.text3, marginTop: 3 }}>{sub}</div>
                  <div style={{ fontSize: 11, color, marginTop: 8, fontWeight: 600 }}>Go →</div>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}